import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, DialogActions } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { type NurseOption, useSwapRequest } from "../../hooks/useSwapRequest";
import { INK, NEUTRAL } from "../../theme/tokens";
import type { NurseInShift, SpecificPreference } from "../../utils/models";
import { NursePanelHeader } from "../../utils/panelComponents";
import { useMobile } from "../../utils/utilFunctions";
import { HeaderButton } from "../headerButton";
import { isRelevantPTO } from "../StaffTable/utils";
import type { SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SwapSide } from "./SwapSide";

type SwapStaffProps = {
	weekIndex: number;
	dayIndex: number;
	shiftIndex: number;
	onClose?: (success?: boolean) => void;
	shift?: NurseInShift;
	handleSwap: (originShiftId: string, targetShiftId: string) => void;
};

const mapNurseOptions = (options: NurseOption[]): SystemAutoCompleteOption<NurseOption>[] => options.map((option) => ({ label: option.label, value: option }));

const mapShiftOptions = (options: NurseInShift[], jobManagementEnabled: boolean): SystemAutoCompleteOption<NurseInShift>[] => {
	const formatShiftLabel = (shift: NurseInShift): string => {
		const dateLabel = shift.startTime.toFormat("MMM D, yyyy");
		const jobPrefix = jobManagementEnabled && shift.jobName ? `${shift.jobName} : ` : "";
		const timeLabel = shift.startTime && shift.endTime ? `${shift.startTime.toFormat("h:mm a")} - ${shift.endTime.toFormat("h:mm a")}` : "";
		return `${jobPrefix} ${dateLabel} ${timeLabel}`;
	};
	return options.map((shift) => ({ label: formatShiftLabel(shift), value: shift }));
};

export const SwapStaff = ({ weekIndex, dayIndex, shiftIndex, onClose, shift, handleSwap }: SwapStaffProps) => {
	const { getShiftDate, chosenDepartment } = useHospitalData();
	const { isMobile } = useMobile();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

	const defaultDate = useMemo(() => {
		if (shift?.startTime) {
			return shift.startTime.startOf("day");
		}
		return getShiftDate(weekIndex, shiftIndex, dayIndex);
	}, [shift, dayIndex, getShiftDate, shiftIndex, weekIndex]);

	const [dates, setDates] = useState<{ origin: DateTime | null; swap: DateTime | null }>({ origin: defaultDate, swap: null });

	useEffect(() => {
		setDates({ origin: defaultDate, swap: null });
	}, [defaultDate]);

	const dateRange = useMemo(() => {
		const previousMonthStart = DateTime.now().startOf("week");
		const nextMonthEnd = defaultDate.plus({ months: 2 }).endOf("month");
		return { start: previousMonthStart, end: nextMonthEnd };
	}, [defaultDate]);

	const { origin, swap, hasShiftInDate } = useSwapRequest({
		originDate: dates.origin,
		swapDate: dates.swap,
		initialShift: shift,
	});

	const hasPtoOnDate = (preferences: SpecificPreference[] | undefined, date: DateTime | null): boolean =>
		Boolean(preferences && date && preferences.some((pref) => isRelevantPTO(pref, date, chosenDepartment.dayStart)));

	const originHasPtoOnSwapDate =
		Boolean(origin.nurse && swap.shift) && hasPtoOnDate(origin.nurse?.nurse.employeeSpecificPreferences, swap.shift?.startTime ?? null);

	const swapHasPtoOnOriginDate =
		Boolean(swap.nurse && origin.shift) && hasPtoOnDate(swap.nurse?.nurse.employeeSpecificPreferences, origin.shift?.startTime ?? null);

	const hasPtoConflict = Boolean(originHasPtoOnSwapDate || swapHasPtoOnOriginDate);

	const canSubmit = Boolean(origin.nurse && swap.nurse && origin.shift && swap.shift && origin.shift.id !== swap.shift.id && !hasPtoConflict);

	const handleSubmit = useCallback(() => {
		if (!canSubmit || !origin.shift || !swap.shift) return;

		handleSwap(origin.shift.id, swap.shift.id);
		onClose?.(true);
	}, [canSubmit, origin.shift, swap.shift, handleSwap, onClose]);

	const nurseAutoCompleteOptions = useMemo(() => mapNurseOptions(origin.nurseOptions), [origin.nurseOptions]);
	const swapNurseAutoCompleteOptions = useMemo(() => mapNurseOptions(swap.nurseOptions), [swap.nurseOptions]);
	const originShiftAutoCompleteOptions = useMemo(() => mapShiftOptions(origin.shiftOptions, jobManagementEnabled), [origin.shiftOptions, jobManagementEnabled]);
	const swapShiftAutoCompleteOptions = useMemo(() => mapShiftOptions(swap.shiftOptions, jobManagementEnabled), [swap.shiftOptions, jobManagementEnabled]);

	return (
		<>
			<NursePanelHeader
				shiftNo={shiftIndex}
				weekIndex={weekIndex}
				dayNo={dayIndex}
				isMasterSchedule={false}
				title={"Swap Staff"}
				titleCss={{ paddingBottom: "4px" }}
			/>
			<Box
				sx={{
					display: "grid",
					gap: isMobile ? 0 : 3,
					gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
					position: "relative",
				}}
			>
				<SwapSide
					title="Origin"
					side={origin}
					date={dates.origin}
					referenceDate={dates.origin}
					onDateChange={(newValue) => setDates({ ...dates, origin: newValue })}
					nurseOptions={nurseAutoCompleteOptions}
					shiftOptions={originShiftAutoCompleteOptions}
					hasShiftInDate={hasShiftInDate}
					dateRange={dateRange}
					disabled={!!shift}
					errorText={originHasPtoOnSwapDate ? "This staff has PTO on the selected date" : undefined}
					sx={isMobile ? { paddingBottom: 0 } : undefined}
					jobManagementEnabled={jobManagementEnabled}
				/>
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "row", md: "column" },
						alignItems: "center",
						justifyContent: "center",
						position: "relative",
						px: { xs: 0, md: 1.5 },
						py: { xs: 1, md: 0 },
					}}
				>
					<Box
						sx={{
							position: "absolute",
							top: { xs: "50%", md: 0 },
							bottom: { xs: "auto", md: 0 },
							left: { xs: 0, md: "50%" },
							right: { xs: 0, md: "auto" },
							transform: { xs: "translateY(-50%)", md: "translateX(-50%)" },
							width: { xs: "100%", md: "1px" },
							borderTop: { xs: `1px dashed ${NEUTRAL[300]}`, md: "none" },
							borderLeft: { xs: "none", md: `1px dashed ${NEUTRAL[300]}` },
						}}
					/>
					<Box
						sx={{
							position: "relative",
							backgroundColor: "white",
							p: 1,
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<SwapHorizIcon sx={{ fontSize: 24, color: INK.SECONDARY }} />
					</Box>
				</Box>
				<SwapSide
					sx={isMobile ? { paddingTop: 0 } : undefined}
					title="Swap With"
					side={swap}
					date={dates.swap}
					disabled={!origin.shift}
					referenceDate={dates.origin}
					onDateChange={(newValue) => newValue?.isValid && setDates({ ...dates, swap: newValue })}
					nurseOptions={swapNurseAutoCompleteOptions}
					shiftOptions={swapShiftAutoCompleteOptions}
					hasShiftInDate={hasShiftInDate}
					dateRange={dateRange}
					errorText={swapHasPtoOnOriginDate ? "This staff has PTO on the selected date" : undefined}
					jobManagementEnabled={jobManagementEnabled}
				/>
			</Box>
			<DialogActions
				sx={{
					p: "12px 16px",
					justifyContent: "flex-end",
					flex: "0 0 auto",
					gap: 2,
				}}
			>
				<HeaderButton onPress={() => onClose?.(false)} state="off" text="Cancel" disabled={false} secondary={true} />
				<HeaderButton onPress={handleSubmit} state="on" text="Submit" disabled={!canSubmit} />
			</DialogActions>
		</>
	);
};
